import React, { useCallback, useState } from 'react';
import { Header, Filters, SummaryCards, DataTable, SheetImport, DonorExport, NewAccounts, SendsExport, CostCaps } from '../../components';
import type { CostCapDraft } from '../../components';
import { useBroadcasts } from '../../hooks/useBroadcasts';
import { downloadAccountsExport } from '../../lib/api';
import './Dashboard.css';

export const Dashboard: React.FC = () => {
  const {
    broadcasts,
    accounts,
    loading,
    error,
    filters,
    setFilters,
    fetchBroadcasts,
    clearFilters,
    stats,
    isAdmin,
    isSuperAdmin,
    pricingModelOptions,
    handlePricingModelChange,
    savePricingModel,
    resetPricingModel,
    totalSbCost,
    accountPricingModel,
    saveAccountPricingModel,
    hideDays,
    unhideDays,
    costCaps,
    costCapsError,
    addCostCap,
    editCostCap,
    removeCostCap,
  } = useBroadcasts();

  // "Cap selected" in the table opens the caps panel's add form with the range.
  const [capDraft, setCapDraft] = useState<CostCapDraft | null>(null);
  const handleCapDates = useCallback((start_date: string, end_date: string) => {
    setCapDraft({ start_date, end_date });
  }, []);
  const clearCapDraft = useCallback(() => setCapDraft(null), []);
  // Caps belong to one account, so they're only offered with an account selected.
  const showCostCaps = isAdmin && !!filters.account && filters.account !== 'all';

  const [view, setView] = useState<'dashboard' | 'sheet-import' | 'donor-export' | 'new-accounts' | 'sends-export'>('dashboard');

  // The accounts CSV downloads straight from the admin row -- one query, a few
  // seconds, and nothing to configure, so it needs no screen of its own.
  const [accountsExporting, setAccountsExporting] = useState(false);
  const [accountsExportError, setAccountsExportError] = useState<string | null>(null);

  const handleAccountsExport = async () => {
    setAccountsExportError(null);
    setAccountsExporting(true);
    try {
      await downloadAccountsExport();
    } catch (err) {
      setAccountsExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setAccountsExporting(false);
    }
  };

  // Views below are reachable only from admin-gated buttons, but check here too
  // so a stale view can't survive a permission change.
  if (view === 'new-accounts' && isAdmin) {
    return (
      <div className="dashboard">
        <div className="container">
          <Header
            title="True Blue Analytics"
            subtitle="Text Rental Dashboard"
          />
          <NewAccounts
            pricingModelOptions={pricingModelOptions}
            onClose={() => setView('dashboard')}
          />
        </div>
      </div>
    );
  }

  if (view === 'sheet-import') {
    return (
      <div className="dashboard">
        <div className="container">
          <Header
            title="True Blue Analytics"
            subtitle="Text Rental Dashboard"
          />
          <SheetImport onClose={() => setView('dashboard')} />
        </div>
      </div>
    );
  }

  if (view === 'donor-export') {
    return (
      <div className="dashboard">
        <div className="container">
          <Header
            title="True Blue Analytics"
            subtitle="Text Rental Dashboard"
          />
          <DonorExport
            accounts={accounts}
            defaultAccount={filters.account}
            onClose={() => setView('dashboard')}
          />
        </div>
      </div>
    );
  }

  if (view === 'sends-export' && isAdmin) {
    return (
      <div className="dashboard">
        <div className="container">
          <Header
            title="True Blue Analytics"
            subtitle="Text Rental Dashboard"
          />
          <SendsExport
            accounts={accounts}
            onClose={() => setView('dashboard')}
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="container">
          <Header
            title="True Blue Analytics"
            subtitle="Text Rental Dashboard"
            accountName={filters.account}
          />
          <div className="error-message">
            <p>Error: {error}</p>
            <button onClick={fetchBroadcasts} className="btn btn-primary">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        <Header
          title="True Blue Analytics"
          subtitle="Text Rental Dashboard"
          accountName={filters.account}
        />

        <p className="dashboard-disclaimer">
          <strong>Disclaimer:</strong> Please note dashboards don't currently account for
          adjustments or spending caps, and may contain errors. If dashboard displays
          discrepancies in data, email communication and invoices will prevail. Invoices are
          issued approximately two weeks after send date and final amounts may differ after
          late donations and refunds.
        </p>

        {isAdmin && (
          <div className="dashboard-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setView('new-accounts')}
            >
              New Accounts
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setView('sheet-import')}
            >
              Import Pricing from Sheet
            </button>
            {isSuperAdmin && (
              <button
                className="btn btn-secondary"
                onClick={() => setView('donor-export')}
              >
                Export Donors
              </button>
            )}
            <button
              className="btn btn-secondary"
              disabled={accountsExporting}
              onClick={handleAccountsExport}
            >
              {accountsExporting ? 'Exporting…' : 'Export Accounts'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setView('sends-export')}
            >
              Export All Sends
            </button>
          </div>
        )}

        {accountsExportError && (
          <p className="dashboard-action-error">Accounts export failed: {accountsExportError}</p>
        )}

        <Filters
          filters={filters}
          accounts={accounts}
          onFilterChange={setFilters}
          onApply={fetchBroadcasts}
          onClear={clearFilters}
        />

        <SummaryCards
          stats={stats}
          loading={loading}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          totalSbCost={totalSbCost}
          accountPricingModel={accountPricingModel}
          pricingModelOptions={pricingModelOptions}
          onSaveAccountPricing={saveAccountPricingModel}
        />

        {showCostCaps && (
          <CostCaps
            caps={costCaps}
            error={costCapsError}
            draft={capDraft}
            onDraftConsumed={clearCapDraft}
            onAdd={addCostCap}
            onEdit={editCostCap}
            onDelete={removeCostCap}
          />
        )}

        <DataTable
          data={broadcasts}
          loading={loading}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          pricingModelOptions={pricingModelOptions}
          onPricingModelChange={handlePricingModelChange}
          onSavePricingModel={savePricingModel}
          onResetPricingModel={resetPricingModel}
          onHideDates={hideDays}
          onUnhideDates={unhideDays}
          costCaps={costCaps}
          onCapDates={showCostCaps ? handleCapDates : undefined}
        />
      </div>
    </div>
  );
};
